import { useSettings } from "../settings/context";
import type { Setting } from "../settings/types";
import { Provenance } from "./Provenance";
import { pointAt, useBringIntoView } from "./Row";

/**
 * ═══ UNE SUITE D'INTERRUPTEURS EN GRILLE DE PASTILLES ═══
 *
 * Les lignes du récapitulatif de la confirmation sont onze interrupteurs à la suite,
 * sous un même sous-titre : onze lignes d'un libellé et d'un bouton. En pastilles, elles
 * tiennent en trois lignes et se lisent d'un coup d'œil. Voir `settings/layout.ts`.
 *
 * ⚠️ CE QUE LA LIGNE DISAIT, LA GRILLE LE DIT ENCORE :
 *   — l'état EFFECTIF, défaut compris (une pastille allumée tant que personne ne l'a
 *     éteinte, comme l'interrupteur) ;
 *   — l'aide, en bulle au survol de la pastille ;
 *   — la PROVENANCE, mais seulement là où elle apprend quelque chose : une pastille
 *     surchargée pour une langue ou une typologie reprend sa ligne de provenance sous la
 *     grille, avec de quoi retirer la surcharge. Sur la version de référence, aucune
 *     ligne : il n'y a rien en dessous.
 */
export function Chips({
  section,
  settings,
  aimed,
}: {
  section: string;
  settings: ReadonlyArray<Setting>;
  aimed: boolean;
}) {
  const { value, write, detail } = useSettings();
  const ref = useBringIntoView<HTMLDivElement>(aimed);
  const target = settings[0]?.target;
  const overridden = settings.filter((s) => {
    const level = detail(s.key).level;
    return level !== "default" && level !== "unset";
  });

  return (
    <div
      ref={ref}
      className={aimed ? "row row--aimed" : "row"}
      data-set={settings.map((s) => s.key).join(" ")}
      onMouseEnter={() => pointAt(target)}
      onMouseLeave={() => pointAt(undefined)}
    >
      <div className="row__l">
        <span>{section}</span>
      </div>
      <div className="pick" role="group" aria-label={section}>
        {settings.map((s) => {
          const current = value(s.key);
          const on =
            typeof current === "boolean" ? current : s.onByDefault === true;
          return (
            <button
              type="button"
              key={s.key}
              aria-pressed={on}
              title={s.help}
              onClick={() => write(s.key, !on)}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      {overridden.map((s) => (
        <div className="chips__o" key={s.key}>
          <span>{s.label}</span>
          <Provenance settingKey={s.key} />
        </div>
      ))}
    </div>
  );
}
